import React, { useState, useRef, useCallback } from "react";
import { settings } from "@tumbodash/browserPackages";

export const C = {
	bg:        "#080f0f",
	surface:   "#0d1a1a",
	elevated:  "#152525",
	subtle:    "#1e3535",
	chrome:    "#4ec9b7",
	accent:    "#ff6b6b",
	reward:    "#ffe66d",
	text:      "#f0f0f0",
	textSec:   "#8a9e9e",
	textMuted: "#4a6868",
	red:       "#e05252",
};

export const BORDER = "1px solid rgba(78, 201, 183, 0.12)";

export function Logo() {
	return (
		<span style={{ fontStyle: "italic", fontFamily: "Figtree, sans-serif", fontWeight: 400 }}>
			<span style={{ color: C.accent }}>T</span>
			<span style={{ color: C.text }}>umbo</span>
			<span style={{ color: C.chrome }}>D</span>
			<span style={{ color: C.text }}>ash</span>
		</span>
	);
}

export function HoldToLeave() {
	const [progress, setProgress] = useState(0);
	const [holding,  setHolding]  = useState(false);
	const rafRef   = useRef(null);
	const startRef = useRef(null);

	const cleanup = useCallback(() => {
		if (rafRef.current) cancelAnimationFrame(rafRef.current);
		rafRef.current = startRef.current = null;
		setHolding(false);
		setProgress(0);
	}, []);

	const tick = useCallback((t) => {
		if (startRef.current == null) startRef.current = t;
		const pct = Math.min((t - startRef.current) / 2000, 1);
		setProgress(pct);
		if (pct < 1) {
			rafRef.current = requestAnimationFrame(tick);
		} else {
			cleanup();
			window.location.href = settings.appUrl;
		}
	}, [cleanup]);

	const begin = useCallback((e) => {
		e.preventDefault();
		if (rafRef.current) return;
		setHolding(true);
		startRef.current = null;
		rafRef.current = requestAnimationFrame(tick);
	}, [tick]);

	return (
		<button
			style={{
				position:     "relative",
				overflow:     "hidden",
				width:        "100%",
				padding:      "14px 0",
				background:   C.elevated,
				border:       `1px solid ${C.red}40`,
				borderRadius: 8,
				color:        C.red,
				fontWeight:   700,
				fontSize:     14,
				cursor:       "pointer",
				touchAction:  "manipulation",
			}}
			onPointerDown={begin}
			onPointerUp={cleanup}
			onPointerLeave={cleanup}
			onPointerCancel={cleanup}
		>
			<span style={{ position: "relative", zIndex: 1 }}>
				{holding ? "Keep holding…" : "Hold to Leave"}
			</span>
			<span style={{
				position:   "absolute",
				inset:      0,
				width:      `${progress * 100}%`,
				background: `${C.red}28`,
				transition: holding ? "none" : "width 0.15s ease",
			}} />
		</button>
	);
}

/**
 * Full-screen side menu panel.
 * `position` defaults to "fixed" (correct for the type3 overlay which sits in a
 * small #overlay-root container). Pass "absolute" for DOM games whose mount div
 * already covers the full viewport.
 */
export function GameSideMenu({ gameName, instructions, modifiers = [], onClose, position = "fixed" }) {
	return (
		<div style={{
			position,
			inset:          0,
			zIndex:         200,
			background:     "rgba(8, 15, 15, 0.88)",
			backdropFilter: "blur(6px)",
			display:        "flex",
			flexDirection:  "column",
			padding:        "0 0 36px",
			fontFamily:     "system-ui, sans-serif",
		}}>
			{/* Top row */}
			<div style={{
				display:        "flex",
				justifyContent: "space-between",
				alignItems:     "center",
				padding:        "12px 16px",
				marginBottom:   16,
			}}>
				<Logo />
				<button
					onClick={onClose}
					style={{ background: "none", border: "none", color: C.textSec, fontSize: 22, cursor: "pointer", padding: 4 }}
				>
					✕
				</button>
			</div>

			{/* Body */}
			<div style={{ color: C.textSec, fontSize: 13, lineHeight: 1.7, flex: 1, overflowY: "auto", padding: "0 20px" }}>
				<div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{gameName}</div>
				<div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 10 }}>How to play</div>
				{instructions.split("\n").filter(Boolean).map((line, i) => (
					<p key={i} style={{ margin: "0 0 6px" }}>{line}</p>
				))}

				{modifiers.filter((m) => m.instructions).map((m) => (
					<div key={m.id} style={{ marginTop: 14 }}>
						<div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
							Modifier — {m.label}
						</div>
						{m.instructions.split("\n").filter(Boolean).map((line, i) => (
							<p key={i} style={{ margin: "0 0 6px" }}>{line}</p>
						))}
					</div>
				))}
			</div>

			{/* Leave button */}
			<div style={{ marginTop: 24, padding: "0 20px" }}>
				<HoldToLeave />
			</div>
		</div>
	);
}
